// src/pages/negocio/CatalogoNegocio.js
//
// CRUD del catálogo del tendero autenticado, contra backend real.
// - Lista de productos (GET /negocios/{miId}/productos).
// - Crear (POST /productos), editar (PUT /productos/{id}), eliminar
//   (DELETE /productos/{id}).
// - Botón "Cargar plantilla" (POST /mi-negocio/productos/plantilla)
//   para bootstrap del catálogo.
// - Botón "Reiniciar catálogo" (DELETE /mi-negocio/productos) con
//   confirmación fuerte.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Button, IconButton, TextField,
  InputAdornment, Grid, Chip, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Divider, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import SearchIcon        from '@mui/icons-material/Search';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteIcon        from '@mui/icons-material/Delete';
import ImportExportIcon  from '@mui/icons-material/ImportExport';
import DeleteSweepIcon   from '@mui/icons-material/DeleteSweep';

import { useApp } from '../../context/AppContext';
import { negocios as apiNegocios } from '../../api/negociosApi';
import { catalogo as apiCatalogo } from '../../api/catalogoApi';
import { formatoCOP } from '../../utils/format';

export default function CatalogoNegocio() {
  const { sesionBackend } = useApp();
  const miNegocioId = sesionBackend?.entidadId;

  const [items, setItems]       = useState([]);
  const [categorias, setCats]   = useState([]);
  const [texto, setTexto]       = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [ok, setOk]             = useState('');

  const [editando, setEditando] = useState(null); // producto o {} para crear
  const [borrando, setBorrando] = useState(null);
  const [confirmReiniciar, setConfirmReiniciar] = useState(false);

  const cargarProductos = useCallback(async () => {
    if (!miNegocioId) return;
    setError('');
    try {
      const r = await apiNegocios.productos(miNegocioId, { pageSize: 500 });
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los productos.');
    } finally { setCargando(false); }
  }, [miNegocioId]);

  useEffect(() => { cargarProductos(); }, [cargarProductos]);

  // Cargar categorías del catálogo maestro (necesitamos el tipo del negocio)
  useEffect(() => {
    if (!miNegocioId) return;
    (async () => {
      try {
        const neg = await apiNegocios.detalle(miNegocioId);
        const tipo = neg?.categoria?.codigo || neg?.Categoria?.Codigo;
        if (!tipo) return;
        const cats = await apiCatalogo.categoriasCatalogo(tipo);
        setCats(Array.isArray(cats) ? cats : []);
      } catch { /* ignore */ }
    })();
  }, [miNegocioId]);

  const filtrados = useMemo(() => {
    if (!texto.trim()) return items;
    const q = texto.toLowerCase();
    return items.filter(p => (p.nombre || p.Nombre || '').toLowerCase().includes(q));
  }, [items, texto]);

  const eliminar = async () => {
    try {
      await apiCatalogo.eliminar(borrando.productoId || borrando.id);
      setBorrando(null);
      setOk('Producto eliminado.');
      cargarProductos();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo eliminar.'); }
  };

  const cargarPlantilla = async () => {
    try {
      const r = await apiCatalogo.cargarPlantilla();
      setOk(`Plantilla cargada: ${r.agregados} agregados, ${r.saltados} ya existían.`);
      cargarProductos();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar la plantilla.'); }
  };

  const reiniciarTodo = async () => {
    try {
      const r = await apiCatalogo.reiniciarTodo();
      setConfirmReiniciar(false);
      setOk(`Se borraron ${r.borrados} productos.`);
      cargarProductos();
    } catch (e) {
      setConfirmReiniciar(false);
      setError(e.mensajeUsuario || 'No se pudo reiniciar el catálogo. Puede haber productos con ventas asociadas.');
    }
  };

  if (cargando) return <CircularProgress />;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Catálogo</Typography>
          <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tus productos</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<ImportExportIcon />} onClick={cargarPlantilla} variant="outlined">
            Cargar plantilla
          </Button>
          <Button startIcon={<DeleteSweepIcon />} color="error" variant="outlined" onClick={() => setConfirmReiniciar(true)}>
            Reiniciar catálogo
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setEditando({})}>
            Nuevo producto
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk('')}>{ok}</Alert>}

      <TextField
        fullWidth
        placeholder="Buscar en tu catálogo…"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
        }}
      />

      {filtrados.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {items.length === 0
                ? 'Aún no tienes productos. Puedes empezar cargando la plantilla del tipo de tu negocio.'
                : 'No hay productos que coincidan.'}
            </Typography>
            {items.length === 0 && (
              <Button variant="contained" onClick={cargarPlantilla}>Cargar plantilla</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filtrados.map((p) => {
            const id     = p.productoId || p.id;
            const nombre = p.nombre || p.Nombre;
            const precio = p.precioVenta ?? p.PrecioVenta ?? p.precio ?? 0;
            const desc   = p.descripcionCorta || p.DescripcionCorta;
            const activo = p.activo ?? p.Activo ?? true;
            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>{nombre}</Typography>
                      {!activo && <Chip label="Inactivo" size="small" color="default" />}
                    </Box>
                    {desc && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {desc}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ color: 'primary.main', mt: 1 }}>
                      {formatoCOP(precio)}
                    </Typography>
                  </CardContent>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 0.5 }}>
                    <IconButton onClick={() => setEditando(p)} size="small"><EditIcon fontSize="small" /></IconButton>
                    <IconButton onClick={() => setBorrando(p)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialog crear/editar */}
      <DialogProducto
        open={!!editando}
        producto={editando}
        categorias={categorias}
        onClose={() => setEditando(null)}
        onSaved={() => { setEditando(null); setOk('Producto guardado.'); cargarProductos(); }}
        onError={(msg) => setError(msg)}
      />

      {/* Dialog eliminar */}
      <Dialog open={!!borrando} onClose={() => setBorrando(null)}>
        <DialogTitle>Eliminar producto</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres eliminar <strong>{borrando?.nombre || borrando?.Nombre}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            El producto se marca como no disponible. Los pedidos históricos no se afectan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBorrando(null)}>Cancelar</Button>
          <Button onClick={eliminar} variant="contained" color="error">Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar reinicio total */}
      <Dialog open={confirmReiniciar} onClose={() => setConfirmReiniciar(false)}>
        <DialogTitle>Reiniciar catálogo</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Esta acción borra TODOS tus productos.</Alert>
          <Typography>
            Se van a eliminar todos los productos de tu negocio. Los productos que ya tienen pedidos o ventas asociadas NO se pueden borrar; si alguno tiene, la operación completa se cancela.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReiniciar(false)}>Cancelar</Button>
          <Button onClick={reiniciarTodo} variant="contained" color="error">Sí, borrar todo</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ═══════════════════════════════════════════════════════════
// DIALOG CREAR / EDITAR PRODUCTO
// ═══════════════════════════════════════════════════════════

function DialogProducto({ open, producto, categorias, onClose, onSaved, onError }) {
  const esNuevo = !producto?.productoId && !producto?.id;
  const [f, setF] = useState(vacio());
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!producto) return;
    if (esNuevo) { setF(vacio()); return; }
    setF({
      nombre:              producto.nombre || producto.Nombre || '',
      descripcion:         producto.descripcion || producto.Descripcion || '',
      precio:              producto.precioVenta ?? producto.PrecioVenta ?? producto.precio ?? '',
      costo:               producto.costo ?? producto.Costo ?? '',
      stock:               producto.stockActual ?? producto.StockActual ?? '',
      stockMinimo:         producto.stockMinimo ?? producto.StockMinimo ?? '',
      unidad:              producto.unidadAbreviatura || producto.UnidadAbreviatura || 'und',
      codigoBarras:        producto.codigoBarras || producto.CodigoBarras || '',
      categoriaCatalogoId: producto.categoriaCatalogoId ?? producto.CategoriaCatalogoId ?? '',
      marcaNombre:         producto.marcaNombre || producto.MarcaNombre || '',
      disponible:          producto.activo ?? producto.Activo ?? true,
      fotoUrl:             producto.imagenUrl || producto.ImagenUrl || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producto?.productoId, producto?.id, open]);

  const upd = (k) => (e) => setF(s => ({ ...s, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        nombre:              f.nombre.trim(),
        descripcion:         f.descripcion || null,
        precio:              parseFloat(f.precio) || 0,
        costo:               f.costo === '' ? null : parseFloat(f.costo),
        fotoUrl:             f.fotoUrl || null,
        disponible:          !!f.disponible,
        stock:               f.stock === '' ? null : parseFloat(f.stock),
        stockMinimo:         f.stockMinimo === '' ? null : parseFloat(f.stockMinimo),
        unidad:              f.unidad || 'und',
        codigoBarras:        f.codigoBarras || null,
        categoriaCatalogoId: f.categoriaCatalogoId ? parseInt(f.categoriaCatalogoId, 10) : null,
        marcaNombre:         f.marcaNombre || null,
      };
      if (esNuevo) await apiCatalogo.crear(payload);
      else         await apiCatalogo.actualizar(producto.productoId || producto.id, payload);
      onSaved();
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo guardar el producto.');
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onClose={() => !guardando && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{esNuevo ? 'Nuevo producto' : 'Editar producto'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label="Nombre" value={f.nombre} onChange={upd('nombre')} required />
          <TextField label="Descripción" value={f.descripcion} onChange={upd('descripcion')} multiline minRows={2} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Precio de venta" value={f.precio} onChange={upd('precio')} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Costo" value={f.costo} onChange={upd('costo')} />
            </Grid>
          </Grid>

          {categorias.length > 0 && (
            <TextField
              select label="Categoría" value={f.categoriaCatalogoId}
              onChange={upd('categoriaCatalogoId')} required
            >
              {categorias.map(c => (
                <MenuItem key={c.id || c.Id} value={c.id || c.Id}>
                  {(c.categoriaPadreId || c.CategoriaPadreId) ? '  · ' : ''}{c.nombre || c.Nombre}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField label="Marca (opcional)" value={f.marcaNombre} onChange={upd('marcaNombre')} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Stock actual" value={f.stock} onChange={upd('stock')} helperText="Vacío = sin conteo" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Stock mínimo" value={f.stockMinimo} onChange={upd('stockMinimo')} helperText="Alerta de reposición" />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select fullWidth label="Unidad" value={f.unidad || 'und'} onChange={upd('unidad')}
              >
                {['und','kg','g','l','ml','doc','pqt'].map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Código de barras" value={f.codigoBarras} onChange={upd('codigoBarras')} />
            </Grid>
          </Grid>

          <TextField
            label="URL de foto (opcional)"
            value={f.fotoUrl} onChange={upd('fotoUrl')}
            helperText="Subir fotos desde el web viene en una tanda futura. Por ahora puedes pegar una URL."
          />

          <FormControlLabel
            control={<Switch checked={f.disponible} onChange={upd('disponible')} />}
            label={f.disponible ? 'Disponible para venta' : 'Inactivo'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button onClick={guardar} variant="contained" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function vacio() {
  return {
    nombre: '', descripcion: '', precio: '', costo: '',
    stock: '', stockMinimo: '', unidad: 'und', codigoBarras: '',
    categoriaCatalogoId: '', marcaNombre: '',
    disponible: true, fotoUrl: '',
  };
}
